import { fetchOrganizations } from 'admin/actions/organization';
import { fetchShops } from 'admin/actions/shops';
import { getInvitations, inviteUser, resendInvitation } from 'admin/actions/user';
import { Button } from 'admin/components/atoms/Buttons';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import Select from 'admin/components/atoms/Select';
import Table from 'admin/components/atoms/Table';
import TextBox from 'admin/components/atoms/TextBox';
import ApiResults from 'admin/components/molecules/ApiResults';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import Pager from 'admin/components/shared/Pager';
import { allAllowed, shopRoles } from 'admin/routes';
import { formatDate, toQueryParams } from 'admin/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { testEmailAllowedLetters, validateEmail } from '../../utils/email';

const FormWrapper = styled.div`
  display: grid;

  ${Labeled} {
    display: grid;
    grid-template-columns: 1.5fr 4fr;
    justify-items: left;
    margin-top: 10px;
    align-items: center;
  }

  ${Labeled} > ${Label} {
    grid-column: 1;
  }

  ${TextBox} {
    width: 400px;
  }

  ${Select} {
    width: 400px;
  }
`;

const InviteUser = ({ user }) => {
  const [email, setEmail] = useState('');
  const [shopOptions, setShopOptions] = useState([]);
  const [orgOptions, setOrgOptions] = useState([]);
  const [selectedOrgOption, setSelectedOrgOption] = useState({
    label: `${user.organization.name}(${user.organization.code})`,
    value: user.organization,
  });
  const [selectedRoleOption, setSelectedRoleOption] = useState(null);
  const [selectedShopOption, setSelectedShopOption] = useState([]);
  const [invitations, setInvitations] = useState({ rows: [] });
  const [messages, setMessages] = useState(null);
  const [selectedRoleErrMsg, setSelectedRoleErrMsg] = useState(null);
  const [emailErrMsg, setEmailErrMsg] = useState(null);

  const selectableRoles = allAllowed.slice(allAllowed.indexOf(user.role));
  const roleOptions = selectableRoles.map((role) => ({
    label: role,
    value: role,
  }));
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '各種設定', href: '', isCurrentPage: true },
    { value: '管理ユーザ設定', href: '/settings/admin-users', isCurrentPage: false },
    { value: '新規作成・登録状況', href: '', isCurrentPage: true },
  ];
  const defaultPagination = {
    current: 1,
    per_page: 30,
    max_page: 1,
    has_prev: false,
    has_next: false,
  };

  useEffect(() => {
    fetchShops().then((s) => {
      setShopOptions(
        s.map((row) => ({
          label: row.name,
          value: row.id,
        })) ?? []
      );
    });
    getInvitations('page=1&per_page=30').then((json) => setInvitations(json));
    const params = toQueryParams({ per_page: 10000, with_all_members: true });
    fetchOrganizations(params).then((o) => {
      setOrgOptions(
        o.rows?.map((org) => ({
          label: `${org.name}(${org.code})`,
          value: org,
        })) ?? []
      );
    });
  }, []);

  const onChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const onChangeOrgSelector = (org) => {
    setSelectedShopOption(null);
    setSelectedOrgOption(org);
    fetchShops(`organization_code=${org.value.code}`).then((s) => {
      setShopOptions(
        s.map((row) => ({
          label: row.name,
          value: row.id,
        }))
      );
    });
    getInvitations(`organization_code=${org.value.code}&page=1&per_page=30`).then((json) =>
      setInvitations(json)
    );
  };

  const onChangeRoleSelector = (roleOpt) => {
    setSelectedRoleOption(roleOpt);
  };

  const onChangeShopSelector = (shopOpt) => {
    setSelectedShopOption(shopOpt);
  };

  const flashApiResult = () => {
    setTimeout(() => setMessages(null), 5000);
  };

  const hasShop = (role) => shopRoles.indexOf(role) >= 0;

  const responseHandler = (response) => {
    if (response.ok) {
      setMessages([{ text: '管理画面への招待メールの送信に成功しました。' }]);
      getInvitations('page=1&per_page=30').then((json) => setInvitations(json));
    } else {
      response.json().then((json) => {
        const errorDescription = {
          email_conflict: '既にこのメールアドレスで登録している管理ユーザが存在します。',
          temporarily_unavailable: '管理画面への招待メールの送信に失敗しました。',
          shop_admin_user_requires_shop:
            '店舗管理ユーザは少なくとも1つの管理店舗を持つ必要があります。',
          not_shop_admin_user_must_not_be_linked_to_any_shops:
            '店舗管理ユーザ以外は店舗と紐付けられません。',
          failed_to_send_email: 'メールの送信に失敗しました。',
          forbidden: '権限がありません。',
        };
        setMessages([{ text: `${errorDescription[json.type] || json.message}`, isError: true }]);
      });
    }
    flashApiResult();
  };

  const isSelectedServiceRole = () =>
    selectedRoleOption !== null
      ? selectedRoleOption.value === 'SERVICE-MANAGER' ||
        selectedRoleOption.value === 'SERVICE-STAFF'
      : null;

  const isPocketChangeDomainEmail = (emailStr) => {
    const parsedEmail = emailStr.split('@');
    const [local, domain] = parsedEmail;
    return domain !== undefined && domain === 'pocket-change.jp';
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (email.length > 0) {
      setEmailErrMsg(null);
    } else {
      setEmailErrMsg('Eメールアドレスを入力してください');
      return;
    }

    const isValidEmail = validateEmail(email);
    if (typeof isValidEmail !== 'string') {
      setEmailErrMsg(null);
    } else {
      setEmailErrMsg(isValidEmail);
      return;
    }

    if (selectedRoleOption !== null) {
      setSelectedRoleErrMsg(null);
    } else {
      setSelectedRoleErrMsg('管理権限を選択してください');
      return;
    }
    if (isSelectedServiceRole() === null && !isPocketChangeDomainEmail(email)) return;
    const selectedOrg = selectedOrgOption.value;
    const selectedRole = selectedRoleOption.value;
    const selectedShops = hasShop(selectedRole) ? selectedShopOption.map((shop) => shop.value) : [];
    inviteUser({
      email,
      role: selectedRole,
      shops: selectedShops,
      ...(selectedOrg ? { org: selectedOrg } : null),
    }).then(responseHandler);
  };

  const onResend = (invitationId) => {
    resendInvitation(invitationId).then(responseHandler);
  };

  const onChangePage = (page) => {
    getInvitations(
      `organization_code=${selectedOrgOption.value.code}&page=${page}&per_page=30`
    ).then((json) => setInvitations(json));
  };

  const isVisibleShopSelector = () => selectedRoleOption && hasShop(selectedRoleOption.value);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs} />
      <Label style={{ fontWeight: 'bold' }}>管理ユーザを新規作成</Label>
      <form method="post" onSubmit={onSubmit}>
        <LabeledTextBox
          labelText="メールアドレス"
          name="email"
          id="new-email"
          placeholder="招待メールを送るアドレス"
          syncValue={email}
          onChange={(e) => testEmailAllowedLetters(e.target.value) && onChangeEmail(e)}
          errors={[
            emailErrMsg !== null ? emailErrMsg : null,
            isSelectedServiceRole() &&
              !isPocketChangeDomainEmail(email) &&
              '管理権限が Service の場合は pocket-change.jp ドメイン以外では登録できません。',
          ]}
        />
        <LabeledSelect
          id="orgSelector"
          labelText="組織"
          options={orgOptions}
          onChange={onChangeOrgSelector}
          value={selectedOrgOption}
          placeholder="組織を選んでください"
        />
        <LabeledSelect
          id="shopSelector"
          labelText="管理権限"
          options={roleOptions}
          onChange={(e) => {
            setSelectedRoleErrMsg(null);
            onChangeRoleSelector(e);
          }}
          value={selectedRoleOption}
          placeholder="管理権限を選んでください"
          errors={[selectedRoleErrMsg !== null ? selectedRoleErrMsg : null]}
        />
        {isVisibleShopSelector() ? (
          <LabeledSelect
            id="shopSelector"
            labelText="管理店舗"
            options={shopOptions}
            isMulti="true"
            onChange={onChangeShopSelector}
            value={selectedShopOption}
            placeholder="店舗を選んでください"
          />
        ) : null}
        <Button>送信</Button>
      </form>
      <ApiResults messages={messages} />
      <br />
      <Label style={{ fontWeight: 'bold' }}>送信履歴</Label>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.Cell size="5">メールアドレス</Table.Cell>
            <Table.Cell size="2">管理権限</Table.Cell>
            <Table.Cell size="2">送信者</Table.Cell>
            <Table.Cell size="1">ステータス</Table.Cell>
            <Table.Cell size="1">有効期限</Table.Cell>
            <Table.Cell size="1">操作</Table.Cell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {invitations?.rows?.map((invitation) => (
            <Table.Row key={invitation.id}>
              <Table.Cell>{invitation.email}</Table.Cell>
              <Table.Cell>{invitation.role}</Table.Cell>
              <Table.Cell>{invitation.operator ? invitation.operator.name : null}</Table.Cell>
              <Table.Cell>
                {invitation.invited_user ? `登録済み(${invitation.invited_user.name})` : '未登録'}
              </Table.Cell>
              <Table.Cell>{formatDate(invitation.expires_at)}</Table.Cell>
              <Table.Cell>
                {invitation.invited_user || !invitation.operator ? null : (
                  <div>
                    <Button onClick={() => onResend(invitation.id)}>再送</Button>
                  </div>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row noBorder>
            <Table.Cell colSpan="6" align="center" styles={{ paddingTop: '16px' }}>
              <Pager
                pagination={
                  invitations.pagination !== undefined ? invitations.pagination : defaultPagination
                }
                onChangePage={onChangePage}
              />
            </Table.Cell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </FormWrapper>
  );
};

InviteUser.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(InviteUser);
